import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Send, MessageSquare } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { NativeSelect } from '../ui/NativeSelect';
import { useAuthStore } from '@/store/authStore';

const feedbackSchema = z.object({
  type: z.enum(['bug', 'feature', 'general', 'other']),
  subject: z.string().min(1, 'Subject is required').max(100, 'Subject must be less than 100 characters'),
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000, 'Message must be less than 2000 characters'),
  email: z.string().email('Please enter a valid email').optional().or(z.literal('')),
});

type FeedbackFormInputs = z.infer<typeof feedbackSchema>;

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const feedbackTypes = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'general', label: 'General Feedback' },
  { value: 'other', label: 'Other' },
];

export function FeedbackForm({ isOpen, onClose }: FeedbackFormProps) {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FeedbackFormInputs>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      type: 'general',
      subject: '',
      message: '',
      email: user?.email || '',
    },
  });

  const handleFormSubmit = async (data: FeedbackFormInputs) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage(null);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          email: data.email || user?.email || 'anonymous',
          userName: user?.name || 'Anonymous User',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send feedback');
      }

      setSubmitStatus('success');
      reset();

      // Close modal after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Failed to send feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setSubmitStatus('idle');
    setErrorMessage(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title="Send Feedback"
      description="Help us improve GodMode by sharing your thoughts"
      scrollBehavior="inside"
      animation="scale"
    >
      {submitStatus === 'success' ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Thank you for your feedback!
          </h3>
          <p className="text-muted-foreground">
            We appreciate you taking the time to help us improve.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)} className="stack-spacious">
          {/* Feedback Type */}
          <div className="stack-normal">
            <label className="text-label">Feedback Type *</label>
            <NativeSelect {...register('type')}>
              {feedbackTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </NativeSelect>
          </div>

          {/* Subject */}
          <div className="stack-normal">
            <label className="text-label">Subject *</label>
            <Input
              {...register('subject')}
              placeholder="Brief summary of your feedback"
              error={errors.subject?.message}
            />
          </div>

          {/* Message */}
          <div className="stack-normal">
            <label className="text-label">Message *</label>
            <textarea
              {...register('message')}
              placeholder="Please describe your feedback in detail..."
              className="form-textarea min-h-[150px]"
            />
            {errors.message && (
              <p className="form-error-text mt-2">{errors.message.message}</p>
            )}
          </div>

          {/* Email (optional if not logged in) */}
          {!user && (
            <div className="stack-normal">
              <label className="text-label">
                Your Email <span className="text-muted-foreground">(optional)</span>
              </label>
              <Input
                {...register('email')}
                type="email"
                placeholder="your@email.com"
                error={errors.email?.message}
              />
              <p className="text-muted-xs mt-1">
                Provide your email if you'd like us to follow up
              </p>
            </div>
          )}

          {/* Error Alert */}
          {submitStatus === 'error' && errorMessage && (
            <Alert variant="destructive">
              {errorMessage}
            </Alert>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="icon-sm animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="icon-sm" />
                  Send Feedback
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
